import { useState, useEffect } from 'react';
import { ChevronLeft, Edit, Truck as TruckIcon, User, UserPlus, UserMinus, X, MapPin, Calendar, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SignedLink } from '../common/SignedFile';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { ConfirmModal } from '../common/ConfirmModal';

interface TruckDetailPageProps {
  truckId: string;
}

export function TruckDetailPage({ truckId }: TruckDetailPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [truck, setTruck] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState<any>(null);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [assignedDriverIds, setAssignedDriverIds] = useState<Set<string>>(new Set());
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [availableTrailers, setAvailableTrailers] = useState<any[]>([]);
  const [assignedTrailer, setAssignedTrailer] = useState<any>(null);
  const [assigningTrailer, setAssigningTrailer] = useState(false);
  const [showUnassignTrailerConfirm, setShowUnassignTrailerConfirm] = useState(false);

  useEffect(() => {
    loadTruckData();
  }, [truckId]);

  async function loadTruckData() {
    try {
      // Load truck data by id with company info
      const { data: truckData, error: truckError } = await supabase
        .from('trucks')
        .select('*, company:companies(id, name, code)')
        .eq('id', truckId)
        .single();

      if (truckError) throw truckError;
      setTruck(truckData);

      // Load assigned driver
      const { data: assignment } = await supabase
        .from('equipment_assignments')
        .select('driver_id, drivers(*)')
        .eq('truck_id', truckData.id)
        .eq('is_current', true)
        .single();

      if (assignment) {
        setDriver(assignment.drivers);
      }

      // Load all current assignments
      const { data: allAssignments } = await supabase
        .from('equipment_assignments')
        .select('driver_id')
        .eq('is_current', true);

      const assignedIds = new Set(allAssignments?.map(a => a.driver_id).filter(Boolean) || []);
      setAssignedDriverIds(assignedIds);

      // Load all active drivers
      const { data: allDrivers } = await supabase
        .from('drivers')
        .select('*')
        .eq('driver_status', 'active')
        .order('first_name');

      setAvailableDrivers(allDrivers || []);

      // Load all trailers
      const { data: allTrailers } = await supabase
        .from('trailers')
        .select('*')
        .order('unit_number');

      setAvailableTrailers(allTrailers || []);

      // Check if truck has assigned trailer (stored in trailers field)
      if (truckData.trailers) {
        const trailerUnitNumbers = Array.isArray(truckData.trailers) 
          ? truckData.trailers 
          : (typeof truckData.trailers === 'string' ? truckData.trailers.split(',').map((t: string) => t.trim()) : []);
        const { data: assignedTrailers } = await supabase
          .from('trailers')
          .select('*')
          .in('unit_number', trailerUnitNumbers);
        
        if (assignedTrailers && assignedTrailers.length > 0) {
          setAssignedTrailer(assignedTrailers[0]); // For now, just take the first one
        }
      }
    } catch (error) {
      console.error('Error loading truck:', error);
      toast.error('Failed to load truck data');
    } finally {
      setLoading(false);
    }
  }

  const handleAssignDriver = async (driverId: string) => {
    setAssigningDriver(true);
    try {
      // Unassign driver from any previous truck
      await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('driver_id', driverId)
        .eq('is_current', true);

      // Unassign current driver from this truck
      await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('truck_id', truck.id)
        .eq('is_current', true);

      // Assign new driver
      const { error } = await supabase
        .from('equipment_assignments')
        .insert({
          driver_id: driverId,
          truck_id: truck.id,
          is_current: true,
        });

      if (error) throw error;

      toast.success('Driver assigned successfully!');
      setShowDriverModal(false);
      loadTruckData();
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error('Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  const handleUnassignDriver = async () => {
    try {
      const { error } = await supabase
        .from('equipment_assignments')
        .update({ is_current: false, released_at: new Date().toISOString() })
        .eq('truck_id', truck.id)
        .eq('is_current', true);

      if (error) throw error;

      toast.success('Driver unassigned successfully!');
      setShowUnassignConfirm(false);
      setDriver(null);
      loadTruckData();
    } catch (error) {
      console.error('Error unassigning driver:', error);
      toast.error('Failed to unassign driver');
    }
  };


  const handleAssignTrailer = async (trailerId: string) => {
    setAssigningTrailer(true);
    try {
      const trailer = availableTrailers.find(t => t.id === trailerId);
      if (!trailer) return;

      const { error } = await supabase
        .from('trucks')
        .update({ trailers: [trailer.unit_number] })
        .eq('id', truck.id);

      if (error) throw error;

      toast.success('Trailer assigned successfully!');
      setShowTrailerModal(false);
      loadTruckData();
    } catch (error) {
      console.error('Error assigning trailer:', error);
      toast.error('Failed to assign trailer');
    } finally {
      setAssigningTrailer(false);
    }
  };

  const handleUnassignTrailer = async () => {
    try {
      const { error } = await supabase
        .from('trucks')
        .update({ trailers: null })
        .eq('id', truck.id);

      if (error) throw error;

      toast.success('Trailer unassigned successfully!');
      setShowUnassignTrailerConfirm(false);
      setAssignedTrailer(null);
      loadTruckData();
    } catch (error) {
      console.error('Error unassigning trailer:', error);
      toast.error('Failed to unassign trailer');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!truck) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Truck not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/fleet')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <TruckIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Truck {truck.unit_number}</h1>
                  <p className="text-sm text-slate-600">{truck.year} {truck.model}</p>
                  {truck.company && (
                    <p className="text-sm text-slate-500 mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                        {truck.company.code} - {truck.company.name}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate(`/fleet/edit/${truckId}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <Edit className="w-4 h-4" />
              Edit Truck
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Unit Number</label>
                  <p className="text-slate-900 font-semibold">{truck.unit_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">VIN</label>
                  <p className="text-slate-900">{truck.vin}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Year</label>
                  <p className="text-slate-900">{truck.year}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Model</label>
                  <p className="text-slate-900">{truck.model}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">License Plate</label>
                  <p className="text-slate-900">{truck.license_plate || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">PrePass</label>
                  <p className="text-slate-900">{truck.prepass || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Status</label>
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                    truck.truck_status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                    truck.truck_status === 'ALERT' ? 'bg-yellow-100 text-yellow-700' :
                    truck.truck_status === 'SHOP' ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {truck.truck_status || 'ACTIVE'}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Driver Status</label>
                  {driver?.driver_type ? (
                    <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                      driver.driver_type === 'owner_operator' ? 'bg-purple-100 text-purple-700' :
                      driver.driver_type === 'company_driver' ? 'bg-blue-100 text-blue-700' :
                      driver.driver_type === 'rent' ? 'bg-orange-100 text-orange-700' :
                      driver.driver_type === 'buy' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {driver.driver_type === 'owner_operator' ? 'OWNER' :
                       driver.driver_type === 'company_driver' ? 'COMPANY' :
                       driver.driver_type === 'rent' ? 'RENT' :
                       driver.driver_type === 'buy' ? 'BUYING' :
                       driver.driver_type.toUpperCase()}
                    </span>
                  ) : (
                    <p className="text-slate-400">No driver assigned</p>
                  )}
                </div>
                {truck.trailers && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-600">Assigned Trailers</label>
                    <p className="text-slate-900">{truck.trailers}</p>
                  </div>
                )}
                {truck.samsara_vehicle_id && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-slate-600">Samsara Vehicle ID</label>
                    <p className="text-slate-900 font-mono text-sm">{truck.samsara_vehicle_id}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Expiration Dates */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Expiration Dates
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Registration</label>
                  <p className="text-slate-900">{truck.registration_expiration || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">DOT Inspection</label>
                  <p className="text-slate-900">{truck.dot_expiration || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* IFTA & Compliance */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  IFTA & Compliance
                </h2>
              </div>
              <div className="space-y-3">
                {/* IFTA Decal Information */}
                {(truck.ifta_2024 || truck.ifta_2025 || truck.ifta_2026) ? (
                  <div className="space-y-3">
                    {/* Current Year IFTA (2026) */}
                    {truck.ifta_2026 ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">IFTA 2026 ✓</span>
                            <span className="text-sm text-green-700 font-medium">Current Year</span>
                          </div>
                          <p className="text-sm text-slate-700 ml-1">{truck.ifta_2026}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-yellow-800">⚠️ No IFTA 2026 decal. Please update truck information.</p>
                      </div>
                    )}

                    {/* Previous Years */}
                    {(truck.ifta_2024 || truck.ifta_2025) && (
                      <div>
                        <label className="text-sm font-medium text-slate-600 mb-2 block">Previous Years</label>
                        <div className="space-y-2">
                          {truck.ifta_2025 && (
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-slate-500 font-medium w-12 pt-0.5">2025:</span>
                              <span className="text-sm text-slate-700">{truck.ifta_2025}</span>
                            </div>
                          )}
                          {truck.ifta_2024 && (
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-slate-500 font-medium w-12 pt-0.5">2024:</span>
                              <span className="text-sm text-slate-700">{truck.ifta_2024}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">No IFTA decal assigned. Please edit the truck to add decal information.</p>
                  </div>
                )}
                <div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={truck.samsara_enabled} disabled className="w-4 h-4" />
                    <span className="text-sm text-slate-700">Samsara Enabled</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documents
                </h2>
              </div>
              <div className="space-y-3">
                {/* Registration File */}
                <div>
                  <label className="text-sm font-medium text-slate-600">Registration File</label>
                  {truck.registration_file_url ? (
                    <div className="mt-1 flex items-center gap-2">
                      <SignedLink
                        stored={truck.registration_file_url}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Registration
                      </SignedLink>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 mt-1">No file uploaded</p>
                  )}
                </div>

                {/* DOT File */}
                <div>
                  <label className="text-sm font-medium text-slate-600">DOT File</label>
                  {truck.dot_file_url ? (
                    <div className="mt-1 flex items-center gap-2">
                      <SignedLink
                        stored={truck.dot_file_url}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download DOT File
                      </SignedLink>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 mt-1">No file uploaded</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Assigned Driver */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Assigned Driver
              </h2>
              {driver ? (
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-slate-900">{driver.first_name} {driver.last_name}</p>
                    <p className="text-sm text-slate-600">{driver.phone}</p>
                    <p className="text-sm text-slate-600">{driver.email}</p>
                  </div>
                  <button
                    onClick={() => setShowUnassignConfirm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <UserMinus className="w-4 h-4 text-slate-600" />
                    Unassign Driver
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-600 mb-4">No driver assigned</p>
                  <button
                    onClick={() => setShowDriverModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium w-full"
                  >
                    <UserPlus className="w-4 h-4" />
                    Assign Driver
                  </button>
                </div>
              )}
            </div>

            {/* Assigned Trailer */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <TruckIcon className="w-5 h-5" />
                Assigned Trailer
              </h2>
              {assignedTrailer ? (
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-slate-900">Trailer {assignedTrailer.unit_number}</p>
                    <p className="text-sm text-slate-600">{assignedTrailer.make} {assignedTrailer.year}</p>
                    {assignedTrailer.license_plate && (
                      <p className="text-sm text-slate-600">Plate: {assignedTrailer.license_plate}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowUnassignTrailerConfirm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <UserMinus className="w-4 h-4 text-slate-600" />
                    Unassign Trailer
                  </button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-600 mb-4">No trailer assigned</p>
                  <button
                    onClick={() => setShowTrailerModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium w-full"
                  >
                    <UserPlus className="w-4 h-4" />
                    Assign Trailer
                  </button>
                </div>
              )}
            </div>

            {/* GPS Location */}
            {truck.samsara_enabled && truck.samsara_vehicle_id && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  GPS Location
                </h2>
                <p className="text-sm text-slate-600">
                  Location tracking enabled via Samsara
                </p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Assign Driver Modal */}
      {showDriverModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Assign Driver</h3>
              <button
                onClick={() => setShowDriverModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-2">
                {availableDrivers.map((d) => {
                  const isAssigned = assignedDriverIds.has(d.id);
                  return (
                    <button
                      key={d.id}
                      onClick={() => !isAssigned && handleAssignDriver(d.id)}
                      disabled={isAssigned || assigningDriver}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        isAssigned
                          ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-50'
                          : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <p className="font-medium text-slate-900">{d.first_name} {d.last_name}</p>
                      <p className="text-sm text-slate-600">{d.phone}</p>
                      {isAssigned && (
                        <p className="text-xs text-orange-600 mt-1">Already assigned to another truck</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Trailer Modal */}
      {showTrailerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Assign Trailer</h3>
              <button
                onClick={() => setShowTrailerModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-2">
                {availableTrailers.map((trailer) => (
                  <button
                    key={trailer.id}
                    onClick={() => handleAssignTrailer(trailer.id)}
                    disabled={assigningTrailer}
                    className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <p className="font-medium text-slate-900">Trailer {trailer.unit_number}</p>
                    <p className="text-sm text-slate-600">{trailer.make} {trailer.year}</p>
                    {trailer.license_plate && (
                      <p className="text-xs text-slate-500">Plate: {trailer.license_plate}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Driver Confirmation */}
      <ConfirmModal
        isOpen={showUnassignConfirm}
        title="Unassign Driver"
        message={`Are you sure you want to unassign ${driver?.first_name} ${driver?.last_name} from this truck?`}
        confirmText="Unassign"
        cancelText="Cancel"
        onConfirm={handleUnassignDriver}
        onCancel={() => setShowUnassignConfirm(false)}
        type="warning"
      />

      {/* Unassign Trailer Confirmation */}
      <ConfirmModal
        isOpen={showUnassignTrailerConfirm}
        title="Unassign Trailer"
        message={`Are you sure you want to unassign Trailer ${assignedTrailer?.unit_number} from this truck?`}
        confirmText="Unassign"
        cancelText="Cancel"
        onConfirm={handleUnassignTrailer}
        onCancel={() => setShowUnassignTrailerConfirm(false)}
        type="warning"
      />
    </div>
  );
}
